import PAGES from './appLinkPages.json';
import { isReservedHandle } from './reservedHandles';

/**
 * Where does an admin-typed link go? ONE resolver for the GOES TO / OPENS ON TAP picker, the
 * publish check and the server (Services/AppLinkResolver.cs runs the same rules on save; the
 * two stay in sync like the reserved-handles lists, and `appLinkPages.json` is the shared
 * fixture of static pages — the C# test reads it).
 *
 *   resolveAppLink(value, { mode, catalog }) → { kind, label, note, ok, warn, path, pending }
 *
 * `kind` is one of PAGE · EVENT · CLUB · PRO · PROFILE · CITY · WEBSITE · LINK · CHECK.
 * `ok:false` blocks publish. `warn` is the amber caution (staff-only pages). `path` is what
 * gets SAVED (an own-domain URL is stored as its path). `pending` means the rules alone cannot
 * say — an event / club / Pro page / handle has to be looked up — and the picker asks the
 * server (`GET app-notices/admin/links/resolve`) before it trusts the row.
 *
 * `catalog` is the list the picker already loaded (`{ kind, path, label, sub, img }`); a path
 * found there resolves without a round trip. design_handoff_app_notices, "Link picker".
 */
export const STATIC_PAGES = PAGES.map(([path, label]) => ({ kind: 'PAGE', path, label }));

export const KIND_COLORS = {
  PAGE: ['#0A0A0A', '#fff'],
  EVENT: ['#22ECFB', '#000'],
  CLUB: ['#B5B1FF', '#000'],
  PRO: ['#FAE1B4', '#000'],
  PROFILE: ['#FCAA6D', '#000'],
  CITY: ['#FF33C6', '#000'],
  WEBSITE: ['#127BFD', '#fff'],
  LINK: ['#E5E5E5', '#000'],
  CHECK: ['#EF4444', '#fff'],
};

export const GROUP_TITLES = { PAGE: 'PAGES', EVENT: 'EVENTS', CLUB: 'CLUBS', PRO: 'PRO PAGES', PROFILE: 'PEOPLE', CITY: 'CITIES' };

/** What a blank value means on each surface. */
export const BLANK_FALLBACK = {
  sheet: { kind: 'PAGE', label: 'Home', note: 'Blank opens home.', ok: true, warn: false, path: '/' },
  push: { kind: 'PAGE', label: 'Notifications', note: 'Blank opens notifications.', ok: true, warn: false, path: '/notifications' },
};

const OWN_HOST = /^https?:\/\/(www\.)?(heyportal\.app|partyresume\.com)(\/[^\s]*)?$/i;
const OPAQUE = /^\/(portals|gatherings|p|ticket|gt|i|join)\/[\w-]+(\/[\w-]+)*$/;
const titleWords = (s) => s.replace(/-/g, ' ');

const r = (kind, label, note, ok = true, warn = false, extra = {}) => ({ kind, label, note, ok, warn, path: null, pending: false, ...extra });

/** `?query`, `#hash` and a trailing `/` are not part of the address. */
export function normalizePath(v) {
  return (v || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
}

export function resolveAppLink(raw, { mode = 'sheet', catalog = [] } = {}) {
  let v = (raw || '').trim();
  if (!v) return { ...BLANK_FALLBACK[mode] || BLANK_FALLBACK.sheet, pending: false };

  let ownNote = '';
  const own = v.match(OWN_HOST);
  if (own) {
    v = own[3] || '/';
    ownNote = `Saved as ${normalizePath(v)}. `;
  } else if (/^(https?:\/\/|mailto:|tel:)/i.test(v)) {
    try {
      const u = new URL(v);
      const label = u.protocol.startsWith('http') ? u.hostname.replace(/^www\./, '') : v.replace(/^(mailto|tel):/i, '');
      if (u.protocol.startsWith('http') && !u.hostname.includes('.')) throw new Error('no host');
      return r('WEBSITE', label, 'Opens outside Portal, in the browser.', true, false, { path: v });
    } catch {
      return r('CHECK', 'Not a working link', 'Check the address.', false, true);
    }
  }

  if (!v.startsWith('/')) {
    if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(v)) return r('CHECK', 'Add https:// to the front', `For a website, use https://${v}`, false, true);
    return r('CHECK', 'Start with / or https://', '/ for a Portal page, https:// for a website.', false, true);
  }

  const p = normalizePath(v);
  const hit = catalog.find((c) => c.path === p) || STATIC_PAGES.find((c) => c.path === p);
  if (hit) return r(hit.kind, hit.label, ownNote + (hit.sub || p), true, false, { path: p });

  let m;
  if ((m = p.match(/^\/square\/([\w-]+)$/))) return r('CITY', `City Portal · ${titleWords(m[1])}`, `${ownNote}Empty if this city has no square yet.`, true, false, { path: p });
  if ((m = p.match(/^\/events\/in\/([\w-]+)(?:\/([\w-]+))?$/))) return r('CITY', `Events in ${titleWords(m[1])}${m[2] ? ` · ${m[2]}` : ''}`, ownNote + p, true, false, { path: p });
  if (/^\/clubs\/[\w-]+(\/(forum|gatherings|manage|revenue))?$/.test(p)) return r('CLUB', p, `${ownNote}Checking this club…`, false, false, { path: p, pending: true });
  if (/^\/e\/[\w-]+(\/[\w-]+)?$/.test(p) || /^\/events\/[\w-]+$/.test(p)) return r('EVENT', p, `${ownNote}Checking this event…`, false, false, { path: p, pending: true });
  if (/^\/pro\/[\w-]+$/.test(p) && !/^\/pro\/(setup|dashboard|desk|claim|closed|deleted)$/.test(p)) return r('PRO', p, `${ownNote}Checking this Pro page…`, false, false, { path: p, pending: true });
  if (OPAQUE.test(p)) return r('LINK', p, `${ownNote}Checked when someone opens it.`, true, false, { path: p });
  if (/^\/admin(\/|$)/.test(p)) return r('PAGE', `Admin · ${p.split('/')[2] || 'today'}`, 'Staff only. Everyone else lands on home.', true, true, { path: p });
  if ((m = p.match(/^\/([\w.-]+)$/))) {
    // A reserved first segment (/login, /onboard, /search…) is a Portal page, never a handle.
    if (isReservedHandle(m[1])) return r('PAGE', `Portal · ${m[1]}`, ownNote + p, true, false, { path: p });
    return r('PROFILE', `@${m[1]}`, `${ownNote}Checking this handle…`, false, false, { path: p, pending: true });
  }
  return r('CHECK', 'Not a Portal page', 'People would land on home.', false, true, { path: p });
}

/** The kind pill colours for a resolution (or a catalog row). */
export const kindColors = (kind) => KIND_COLORS[kind] || KIND_COLORS.LINK;

/** The strip's background: grey when fine, amber on a caution, red when it blocks. */
export const rowBackground = (res) => (!res.ok && !res.pending ? '#FEF2F2' : res.warn ? '#FEF3C7' : '#F7F7F7');
